import * as React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Alert } from "@mui/material";
import { ClientConfig, ClientsService, SystemSettings, SystemSettingsService } from "gen/clients";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { Form, Formik } from "formik";
import { SelectOption } from "@mui/base";
import { useQuery, useMutation } from "react-query";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import * as Yup from "yup";

interface Props {
  clientConfig: ClientConfig;
  onClose: () => void;
  onSuccess: () => void;
}

enum FieldName {
  appBundleId = "appBundleId"
}

interface FormValues {
  [FieldName.appBundleId]: string;
}

const NO_APP_BUNDLE_VALUE = "NO_APP_BUNDLE";

const AssignAppBundleDialog: React.FC<Props> = ({ clientConfig, onClose, onSuccess }) => {
  const { data: systemSettings, isLoading: isLoadingSystemSettings } = useQuery<SystemSettings>(["getSystemSettings"], {
    queryFn: SystemSettingsService.getSystemSettings,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    cacheTime: 0
  });

  const { mutateAsync, error, isLoading: isSaving } = useMutation(ClientsService.updateClientConfig);

  const appBundleOptions: SelectOption<string>[] = React.useMemo(() => {
    const options: SelectOption<string>[] = [
      {
        label: "No app bundle",
        value: NO_APP_BUNDLE_VALUE
      }
    ];

    if (systemSettings?.appBundles) {
      const activeAppBundles = systemSettings.appBundles.filter(bundle => !bundle.isDisabled);
      activeAppBundles.forEach(bundle => {
        options.push({
          label: bundle.name,
          value: bundle.id
        });
      });
    }

    return options;
  }, [systemSettings]);

  const validationSchema = Yup.object().shape({
    [FieldName.appBundleId]: Yup.string()
  });

  const initialValues: FormValues = {
    [FieldName.appBundleId]: clientConfig.appBundleId || NO_APP_BUNDLE_VALUE
  };

  const handleSubmit = React.useCallback(
    async (values: FormValues) => {
      const clientConfigUpdate: ClientConfig = {
        ...clientConfig,
        appBundleId: values.appBundleId === NO_APP_BUNDLE_VALUE ? undefined : values.appBundleId
      };

      await mutateAsync(
        {
          id: clientConfig.id,
          requestBody: clientConfigUpdate
        },
        {
          onSuccess: () => {
            onSuccess();
            onClose();
          }
        }
      );
    },
    [clientConfig, mutateAsync, onSuccess, onClose]
  );

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign App Bundle</DialogTitle>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ isSubmitting, isValid }) => (
          <Form>
            <DialogContent>
              <Stack spacing={3}>
                <Alert severity="warning">
                  Please note that changing the app bundle will affect which applications are available to the client
                  and may modify existing application settings.
                </Alert>
                <SelectOneField
                  name={FieldName.appBundleId}
                  label="App Bundle"
                  options={appBundleOptions}
                  isLoading={isLoadingSystemSettings}
                  variant="outlined"
                />
                {error && <ApiErrorMessage apiError={error} />}
              </Stack>
            </DialogContent>
            <DialogActions>
              <LoadingButton
                type="submit"
                color="primary"
                variant="contained"
                isLoading={isSubmitting || isSaving}
                disabled={!isValid}
              >
                Save
              </LoadingButton>
              <Button onClick={onClose} color="secondary">
                Cancel
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export { AssignAppBundleDialog };
